import useRouter from '@vromo/hooks/useRouter';
import { useStore } from '@vromo/mobx-react';
import { LocalStorage } from '@vromo/models/LocalStorage';
import Fade from '@vromo/ui/components/Fade';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import { TabView } from '@vromo/ui/components/mobile';
import Permissions from '@vromo/ui/components/Permissions';
import { Observer, observer } from 'mobx-react-lite';
import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { Route, Switch } from 'react-router-dom';
import { AutoSizer } from 'react-virtualized';
import { dispatchPageStore, PageContext } from '../stores/local/PageStore';
import { RootStore } from '../stores/RootStore';
import EnterpriseDispatchBanner from './components/EnterpriseDispatchBanner';
import MobileViewTabs from './components/MobileViewTabs';
import { ModalPlaceholder } from './components/modals/PageModals';
import { PageDecorations } from './components/PageDecorations';
import {
    getJobTabStyle,
    getExpandJobsStyle,
    getNewJobButtonStyle,
    pageContainer,
    pageContainerStyle
} from './dispatch.style';

const JobListAndFilter = React.lazy(
    () => import(/* webpackPrefetch: true */ './components/JobList')
);
const SpatulaMap2 = React.lazy(
    () => import(/* webpackPrefetch: true */ '../common/MapboxMap/SpatulaMap2.lazy')
);
const DispatchMap = React.lazy(
    () => import(/* webpackPrefetch: true */ '../common/MapboxMap/DispatchMap.lazy')
);
const Drivers = React.lazy(() => import(/* webpackPrefetch: true */ './components/drivers'));

const DriverChat = React.lazy(
    () => import(/* webpackPrefetch: true */ './components/DriverChat/DriverChat')
);
const EditJobForm = React.lazy(
    () => import(/* webpackPrefetch: true */ './components/EditJobForm/EditJobForm')
);
const NewJobForm = React.lazy(
    () => import(/* webpackPrefetch: true */ './components/NewJobForm/NewJobForm')
);

const DispatchMapWrapper = observer(() => {
    const { viewStore } = useStore<RootStore>('rootStore');
    const MapComponent = LocalStorage.enterpriseDispatch ? DispatchMap : SpatulaMap2;

    return (
        <AutoSizer>
            {({ width, height }) => (
                <Suspense fallback={<div />}>
                    <Observer>
                        {() => (
                            <TabView
                                tab="map"
                                style={{
                                    width,
                                    height,
                                    backgroundColor: 'rgb(64, 64, 64)'
                                }}>
                                <MapComponent
                                    key={`${viewStore.darkMode}`}
                                    {...{ width, height }}
                                />
                            </TabView>
                        )}
                    </Observer>
                </Suspense>
            )}
        </AutoSizer>
    );
});

interface OpenJobFormButtonProps {
    openJobForm: () => void;
    isJobsPanelExpanded: boolean;
}

const OpenJobFormButton = observer(
    ({ openJobForm, isJobsPanelExpanded }: OpenJobFormButtonProps) => (
        <>
            <Permissions minPosition="freemiumUser">
                <a
                    id="new-job-button"
                    onClick={openJobForm}
                    className={getNewJobButtonStyle(isJobsPanelExpanded)}>
                    <FontAwesome icon={'plus'} weight="regular" x2 style={{ color: 'white' }} />
                </a>
            </Permissions>
        </>
    )
);

export const DispatchJobPage = observer(() => {
    const { history } = useRouter();
    const { viewStore } = useStore<RootStore>('rootStore');
    const [expanded, setExpanded] = useState<'jobs' | 'drivers' | null>(null);
    const [newJobModalOpen, setNewJobModalOpen] = useState(false);

    useEffect(() => {
        dispatchPageStore.initPage();
    }, []);

    const isJobsPanelExpanded = expanded === 'jobs';

    const openJobForm = useCallback(() => setNewJobModalOpen(true), []);

    return (
        <PageContext.Provider value={dispatchPageStore}>
            <div className={pageContainer} style={pageContainerStyle}>
                <DispatchMapWrapper />
                <EnterpriseDispatchBanner />
                <Fade in={viewStore.isDetailedView}>
                    <Suspense fallback={<div />}>
                        <Drivers />
                    </Suspense>
                    <Suspense fallback={<div />}>
                        <TabView tab="chat">
                            <DriverChat />
                        </TabView>
                    </Suspense>
                </Fade>
                <PageDecorations />
                <ModalPlaceholder>
                    <Suspense fallback={<div />}>
                        <Route
                            path="/dispatch/edit/:id"
                            children={({ match }) => (
                                <EditJobForm
                                    jobId={match ? parseInt(match.params.id) : 0}
                                    history={history}
                                />
                            )}
                        />
                    </Suspense>
                    <NewJobForm isOpen={newJobModalOpen} close={() => setNewJobModalOpen(false)} />
                </ModalPlaceholder>

                <TabView
                    tab="jobs"
                    className={getJobTabStyle(
                        isJobsPanelExpanded,
                        viewStore.filters.jobs.search,
                        viewStore.isDetailedView
                    )}>
                    <Permissions minPosition="client">
                        <div
                            onClick={() => setExpanded(isJobsPanelExpanded ? null : 'jobs')}
                            className={getExpandJobsStyle(isJobsPanelExpanded)}>
                            <FontAwesome icon={isJobsPanelExpanded ? 'compress' : 'expand'} />
                        </div>
                    </Permissions>
                    <Switch>
                        <Suspense fallback={<div />}>
                            <Route path="*" component={JobListAndFilter} />
                        </Suspense>
                    </Switch>
                    {viewStore.isDetailedView && (
                        <OpenJobFormButton {...{ isJobsPanelExpanded, openJobForm }} />
                    )}
                </TabView>
            </div>
            <MobileViewTabs />
        </PageContext.Provider>
    );
});

export default DispatchJobPage;
